/**
 * Shared utilities for resolving reminder subject navigation.
 * Used by eventMapper (calendar view) and ReminderRow (dashboard view).
 */

import type { AssociationDetail, CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

/**
 * Finds the subject association and corresponding object for a reminder.
 * The `ahoy_reminder_to_subject` association links a reminder to the entity
 * (e.g. a deal) whose date property triggered the reminder.
 */
export function resolveReminderSubject(
  reminder: CrmObject,
  associatedObjects: Record<string, CrmObject>
): {
  subjectAssoc: AssociationDetail | undefined;
  subject: CrmObject | undefined;
  navigationEntityId: string | undefined;
  navigationEntityTypeName: string | undefined;
} {
  const subjectAssoc = reminder.associations?.find(
    a => a.association_type_name === 'ahoy_reminder_to_subject'
  );
  const navigationEntityId = subjectAssoc?.target_entity_id;
  const subject = navigationEntityId
    ? associatedObjects[navigationEntityId]
    : undefined;
  const navigationEntityTypeName = subject?.entity_type_name;
  return {
    subjectAssoc,
    subject,
    navigationEntityId,
    navigationEntityTypeName,
  };
}

/**
 * The owner of a reminder, resolved from the transitive `ahoy_reminder_to_owner`
 * association (materialized from the subject note's owner by
 * TransitiveAssociationCascade). Returns the owner's user entity id, or null
 * when the reminder has no owner — its subject note is unowned, or its subject
 * is not a note (only note subjects propagate an owner via the transitive chain).
 */
export function reminderOwnerEntityId(reminder: CrmObject): string | null {
  const ownerAssoc = reminder.associations?.find(
    a => a.association_type_name === 'ahoy_reminder_to_owner'
  );
  return ownerAssoc?.target_entity_id ?? null;
}

/**
 * Whether a reminder should be shown to the current user on the dashboard /
 * calendar. A reminder is shown when it is unowned (visible to everyone) or its
 * owner is this user; reminders owned by someone else are hidden. This is a
 * declutter filter over data the CRM already authorized the user to read — not a
 * confidentiality boundary.
 *
 * `currentUserEntityId` may be null (the user's entity id has not resolved yet);
 * in that case ownership cannot be proven, so only unowned reminders are shown.
 * Shared between the dashboard and calendar so the rule cannot drift.
 */
export function isReminderVisibleToUser(
  reminder: CrmObject,
  currentUserEntityId: string | null
): boolean {
  const ownerId = reminderOwnerEntityId(reminder);
  return ownerId === null || ownerId === currentUserEntityId;
}

/**
 * The literal value the legacy constant formula (`formula: '"Reminder"'` on
 * ahoy_reminder.ahoy_display_name, removed in #6655) materialized onto every
 * reminder created before ~June 2026. Those reminders carry this string as
 * their stored display name, so it must be treated as "no title set" — never
 * as a real title. Safe to sniff: the property is internal-write and the
 * ReminderDisplayNameCascade always writes "{subject}: {label}", which can
 * never equal the bare literal.
 */
const LEGACY_FORMULA_DISPLAY_NAME = 'Reminder';

/**
 * Final UI display default for `resolveReminderTitle`, shown when neither a
 * real ahoy_display_name nor the subject's name is available. Distinct from
 * `LEGACY_FORMULA_DISPLAY_NAME`, which is a detection sentinel for pre-#6655
 * data — the two constants intentionally share a value today but are not
 * required to stay in sync.
 */
const FALLBACK_REMINDER_TITLE = 'Reminder';

/**
 * Resolve the display title for a reminder. Prefers ahoy_display_name (set
 * by the ReminderDisplayNameCascade); for reminders without a real one —
 * created before the cascade existed, or stamped with the legacy formula
 * constant "Reminder" — falls back to the subject's name, then to the
 * literal string "Reminder".
 *
 * The fallback deliberately does NOT compose "{subject}: {property}" from
 * ahoy_referenced_property: that value is the raw property_type_name (e.g.
 * "ahoy_date"), and rendering internal names to users is the original #6595
 * bug. Only the cascade, which can resolve the human label, composes titles.
 *
 * Shared between eventMapper (calendar) and ReminderRow (dashboard) so the
 * fallback chain can't drift between surfaces.
 */
export function resolveReminderTitle(
  reminder: CrmObject,
  subject: CrmObject | undefined
): string {
  const displayName = getPropertyValue<string>(
    reminder.properties?.ahoy_display_name
  );
  if (displayName && displayName !== LEGACY_FORMULA_DISPLAY_NAME) {
    return displayName;
  }
  const subjectName = getPropertyValue<string>(
    subject?.properties?.ahoy_display_name
  );
  if (subjectName) {
    return subjectName;
  }
  return FALLBACK_REMINDER_TITLE;
}
